import {Injectable} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {ModalErrorService} from './modal-error.service';
import * as Sentry from '@sentry/angular';
import {environment} from '../../../environments/environment';
import {HoomvipStorage} from '../hoomvip-storage/hoomvip-storage.service';

@Injectable({providedIn: 'root'})
export class GlobalErrorService {
  private readonly handledErrors = new WeakSet<object>();
  private readonly silentErrors = new WeakSet<object>();
  private readonly pendingHttpErrors = new Set<unknown>();
  private flushTimer: number | null = null;
  private readonly sentryErrorHandler = environment.sentryActive
    ? Sentry.createErrorHandler()
    : null;

  constructor(private readonly modal: ModalErrorService, private hoomvipStorage: HoomvipStorage) {
  }

  markSilent(error: unknown): void {
    if (error && typeof error === 'object') {
      this.silentErrors.add(error);
      console.error('Error marked as silent:', error);
    }
  }

  private toSentryError(error: unknown): Error {
    if (error instanceof Error) {
      return error;
    }

    const anyErr = error as any;
    const sentryError = new Error(this.extractSentryTitle(error));
    sentryError.name = this.getSentryErrorName(error, anyErr);

    return sentryError;
  }

  private getSentryErrorName(error: unknown, anyErr: any): string {
    if (this.isBackendError(error)) {
      const status = typeof anyErr?.status === 'number' ? anyErr.status : undefined;
      return status != null ? `HttpErrorResponse ${status}` : 'HttpErrorResponse';
    }

    if (this.isBrowserError(error)) {
      return 'BrowserError';
    }

    return 'Error';
  }

  private extractSentryTitle(error: unknown): string {
    if (this.isBackendError(error)) {
      const anyErr = error as any;
      const status = typeof anyErr?.status === 'number' ? anyErr.status : undefined;
      const method = typeof anyErr?.method === 'string' ? anyErr.method : undefined;
      const url = typeof anyErr?.url === 'string' ? anyErr.url : undefined;

      const titleParts: string[] = ['HttpErrorResponse'];

      if (status != null) {
        titleParts.push(String(status));
      }

      if (method) {
        titleParts.push(method);
      }

      if (url) {
        titleParts.push(url);
      }

      return titleParts.join(' ');
    }

    if (this.isBrowserError(error)) {
      return 'BrowserError';
    }

    if (typeof error === 'string') {
      return error.trim() || 'Error';
    }

    const anyErr = error as any;
    if (typeof anyErr?.message === 'string' && anyErr.message.trim()) {
      return anyErr.message.trim();
    }

    return 'Error';
  }
  private isBackendError(error: unknown): boolean {
    const anyErr = error as any;
    return !!anyErr?.status && !!anyErr?.error;
  }

  private isBrowserError(error: unknown): boolean {
    if (!(error instanceof Error)) {
      return false;
    }

    const anyErr = error as any;
    return typeof anyErr?.message === 'string' && !this.isBackendError(error);
  }

  private sendToSentry(error: any): void {
    if (!error || typeof error !== 'object') return;

    const sentryError = this.toSentryError(error);
    const anyErr = error as any;
    const status = typeof anyErr?.status === 'number' ? anyErr.status : undefined;
    const requestUrl = typeof anyErr?.url === 'string' ? anyErr.url : undefined;
    const responseErrors = anyErr?.error?.errors;

    Sentry.withScope((scope) => {
      this.addHttpMetadataToScope(scope, status, requestUrl);
      this.addBackendMetadataToScope(scope, anyErr?.error, responseErrors);
      this.addRawErrorToScope(scope, anyErr);
      Sentry.captureException(sentryError);
    });
  }

  private addHttpMetadataToScope(
    scope: Sentry.Scope,
    status: number | undefined,
    requestUrl: string | undefined
  ): void {
    if (status != null) {
      scope.setTag('http.status_code', String(status));
    }

    if (requestUrl) {
      scope.setTag('http.url', requestUrl);
    }

    const brandingCode = this.getBrandingCodeFromUrl();

    if (brandingCode) {
      scope.setTag('hoomvip_environment', brandingCode);
    }
  }

  private getBrandingCodeFromUrl(): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get(environment.brandingCodeQueryParam)?.trim() || null;
  }

  private addBackendMetadataToScope(
    scope: Sentry.Scope,
    body: any,
    responseErrors: any
  ): void {
    if (responseErrors != null) {
      scope.setExtra('backend.errors', responseErrors);
    }

    if (body != null) {
      scope.setExtra('backend.body', body);
    }
  }

  private addRawErrorToScope(scope: Sentry.Scope, error: any): void {
    scope.setExtra('raw.error', {
      name: error?.name,
      message: error?.message,
      status: error?.status,
      url: error?.url,
    });
  }

  markHandled(error: any): void {
    if (this.isHandled(error)) return;
    if (error && typeof error === 'object') {
      this.handledErrors.add(error);
      this.pendingHttpErrors.delete(error);
    }
  }

  isHandled(error: unknown): boolean {
    return !!error && typeof error === 'object' && this.handledErrors.has(error);
  }

  scheduleHttpError(error: unknown, silent: boolean = false, debounceMs: number = 300): void {
    const anyErr = error as any;
    const status = typeof anyErr?.status === 'number' ? anyErr.status : undefined;

    if (!(error instanceof HttpErrorResponse) && status == null) return;
    if(!error || typeof error !== 'object') return;
    if (this.isHandled(error)) return;

    if (environment.sentryActive && !silent) {
      this.sendToSentry(error);
    }

    this.pendingHttpErrors.add(error);

    if(this.flushTimer != null) return;

    this.flushTimer = window.setTimeout(() => {
      const pending = Array.from(this.pendingHttpErrors);
      this.pendingHttpErrors.clear();
      this.flushTimer = null;

      const messages: string[] = [];

      pending.forEach((pendingError) => {
        if (this.isHandled(pendingError)) return;

        const extracted = this.modal.extractMessages(pendingError);
        extracted.forEach((message) => {
          const normalized = String(message).trim();
          if (normalized) {
            messages.push(normalized);
          }
        });
        this.markHandled(pendingError);
      });

      const uniqueMessages = Array.from(new Set(messages));

      if (uniqueMessages.length) {
        this.modal.enqueue('', uniqueMessages);
      }
    }, debounceMs);
  }

  handleUnexpectedError(error: unknown): void {
    if (this.isHandled(error)) return;
    if (this.isSilent(error)) return;
    if (this.sentryErrorHandler) {
      this.sentryErrorHandler.handleError(error);
    } else {
      console.error('Local Error:', error);
    }
  }

  private isSilent(error: any) {
    if (this.silentErrors.has(error)) {
      console.error('Skipping Sentry error:', error);
      this.silentErrors.delete(error);
      return true;
    }
    console.error('Not Silent:', error);
    return false;
  }
}
